// The page: panels, buttons, undo, sharing, and ad capture.

import { createScene } from "./td-scene.js?v=8872c922ae5b";
import { MODELS, MODEL_ORDER, ADDONS, CATALOG, STOVES, stoveKey, stoveFor, COLORS, MATERIALS, FRAMES, ftin, sizeOf } from "./td-specs.js?v=8872c922ae5b";
import {
  newDesign, placeTemplate, templatesFor, changeSize, resetForSize, changeModel, changeWall, setBackDoor, checks, stats, dims, newId,
  itemSize, snapItem, clampWindow, snapWindow, newWindow, clampJack, findStove, standardJack, jackIsCustom, jackAtStandard, setJackOn, tipiJackHome, extent, contains, build, money,
  encodeDesign, decodeDesign, doorZones, corners, wallLength, stockWindowCount, clearUprights, tipiHasBackDoor,
  optionOf, hangSpot, gearKey, lineOff, lineOn, lineRank, jackSpot, bedSpot, bedUnder, stoveRot,
} from "./td-logic.js?v=8872c922ae5b";
import { planSvg as drawPlan, planCaption, packingList } from "./td-plan.js?v=8872c922ae5b";
import { listSetups, saveSetup, removeSetup, setupName, setupEmail, setupLead, sendSetup, validEmail } from "./td-setups.js?v=8872c922ae5b";
import { CONFIG } from "./td-config.js?v=8872c922ae5b";
import { mount } from "./td-mount.js?v=8872c922ae5b";
import { makeTracker, attribution } from "./td-track.js?v=8872c922ae5b";

// On the store the markup is in a shadow root under the store header; standalone it is the document.
const { root: ROOT } = await mount();
const $ = (s, r = ROOT) => r.querySelector(s);
const $$ = (s, r = ROOT) => [...r.querySelectorAll(s)];
const app = $(".td-root");
const el = (tag, attrs = {}, ...kids) => {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") n.className = v;
    else if (k.startsWith("on")) n.addEventListener(k.slice(2), v);
    else if (k === "html") n.innerHTML = v;
    else n.setAttribute(k, v === true ? "" : v);
  }
  for (const c of kids.flat()) if (c != null && c !== false) n.append(c);
  return n;
};

const STORAGE = "ctc-tent-designer-v2";
const params = new URLSearchParams(location.search);
// The ad and template tools stay off the public page. ?tools=1 (or ?capture=1) brings them back.
const TOOLS = params.get("tools") === "1" || params.get("capture") === "1";
let design;
let selection = null;
let tab = "tent";
const track = makeTracker();
const undoStack = [], redoStack = [];
let lastSaved = null;

function loadInitial() {
  if (location.hash.startsWith("#d=")) {
    try { return decodeDesign(location.hash.slice(3)); } catch { /* a stale link falls through to the default */ }
  }
  if (params.has("tent") || params.has("size") || params.has("layout")) {
    const model = MODELS[params.get("tent")] ? params.get("tent") : "colorado";
    const fallback = MODELS[model].kind === "wall" ? "14x17" : "10x10";
    const size = MODELS[model].sizes[params.get("size")] ? params.get("size") : fallback;
    return newDesign(model, size, params.get("layout") || "cots");
  }
  try {
    const saved = localStorage.getItem(STORAGE);
    if (saved) return decodeDesign(saved);
  } catch { /* private window */ }
  return newDesign("colorado", "14x17", "cots");
}

let scene;
try {
  design = loadInitial();
  scene = createScene($("#view"), $("#overlay"), {
    onMode: renderViews,
    onSelect: select,
    onDragItem: dragItem,
    onDragWindow: dragWindow,
    onDragJack: dragJack,
    onDragEnd: dragEnd,
    onTurntable: (on) => { if (!on) renderCapture(); },
  });
} catch (err) {
  const f = $("#fatal");
  f.hidden = false;
  f.textContent = "This page needs WebGL, the browser feature that draws in 3D. Try a current version of Chrome, Safari, or Edge. We can also size a tent by phone at (303) 399-3232.";
  throw err;
}

// ─── Changes and history ────────────────────────────────────

function commit(next, opts = {}) {
  undoStack.push(JSON.stringify(design));
  if (undoStack.length > 80) undoStack.shift();
  redoStack.length = 0;
  design = next;
  refresh(opts);
}
function refresh(opts = {}) {
  track.step("td_open", { model: design.model, size: design.size });
  track.step("td_tent", { model: design.model, size: design.size }, `tent:${design.model}:${design.size}`);
  scene.setDesign(design, opts);
  if (selection?.kind === "item" && !design.items.some((i) => i.id === selection.id)) selection = null;
  if (selection?.kind === "window" && !design.windows.some((w) => w.id === selection.id)) selection = null;
  if (selection?.kind === "jack" && !design.jack.on) selection = null;
  scene.setSelection(selection);
  const list = checks(design);
  scene.setFlagged(list.filter((c) => c.level === "warn").flatMap((c) => c.ids));
  renderChecks(list);
  renderStats();
  renderSelection();
  renderPanel();
  renderTotal();
  $("#undo").disabled = !undoStack.length;
  $("#redo").disabled = !redoStack.length;
  persist();
}
function persist() {
  const text = encodeDesign(design);
  if (text === lastSaved) return;
  lastSaved = text;
  try { localStorage.setItem(STORAGE, text); } catch { /* private window */ }
  // Waits for a pause, because browsers limit how often a page can change its address.
  clearTimeout(linkTimer);
  linkTimer = setTimeout(() => history.replaceState(null, "", `${location.pathname}${location.search}#d=${text}`), 500);
}
let linkTimer = 0;
function undo() {
  if (!undoStack.length) return;
  redoStack.push(JSON.stringify(design));
  design = JSON.parse(undoStack.pop());
  refresh();
}
function redo() {
  if (!redoStack.length) return;
  undoStack.push(JSON.stringify(design));
  design = JSON.parse(redoStack.pop());
  refresh();
}

let toastTimer = 0;
function toast(text, ms = 4200, action = null) {
  const t = $("#toast");
  t.textContent = text;
  if (action) t.append(el("button", { onclick: () => { t.hidden = true; action.run(); } }, action.label));
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { t.hidden = true; }, ms);
}

// ─── Dragging ───────────────────────────────────────────────

let preDrag = null;
function beginDragSnapshot() {
  if (!preDrag) preDrag = JSON.stringify(design);
}
function dragItem(id, x, z) {
  beginDragSnapshot();
  const it = design.items.find((i) => i.id === id);
  if (!it) return;
  const fromX = it.x, fromZ = it.z;
  const { x: nx, z: nz, rot } = snapItem(design, it, x, z, design.items);
  const dx = nx - fromX, dz = nz - fromZ;
  if (!dx && !dz) return;
  it.x = nx;
  it.z = nz;
  // A piece on the wall turns to face the room as it moves around a corner.
  if (rot != null) it.rot = rot;
  scene.moveItem(it);
  // What rests on it, or under it, goes with it.
  for (const o of design.items) {
    if (o.id === it.id) continue;
    const c = CATALOG[o.type];
    const carried = (c.top && CATALOG[it.type].surface && contains({ ...it, x: fromX, z: fromZ }, o.x, o.z))
      || (c.bedding && CATALOG[it.type].bedTop && contains({ ...it, x: fromX, z: fromZ }, o.x, o.z))
      || (it.type === "stove" && o.type === "mat" && contains(o, fromX, fromZ))
      || (it.type === "mat" && o.type === "stove" && contains({ ...it, x: fromX, z: fromZ }, o.x, o.z));
    if (carried) {
      o.x += dx;
      o.z += dz;
      scene.moveItem(o);
    }
  }
  const stove = findStove(design);
  if (design.jack.link && stove && (stove.id === id || it.type === "mat")) {
    design.jack = { ...design.jack, ...clampJack(design, stove.x, stove.z) };
  }
  scene.refreshDrag();
  renderStatsLive();
}
function dragWindow(id, u, sill) {
  beginDragSnapshot();
  const i = design.windows.findIndex((w) => w.id === id);
  if (i < 0) return;
  const next = snapWindow(design, clampWindow(design, { ...design.windows[i], u, sill }));
  design.windows = design.windows.map((w, k) => (k === i ? next : w));
  scene.setDesign(design, { fit: false });
  renderSelection();
}
function dragJack(x, z) {
  beginDragSnapshot();
  const j = clampJack(design, x, z);
  const stove = findStove(design);
  const under = stove && Math.hypot(stove.x - design.jack.x, stove.z - design.jack.z) < 0.25;
  design.jack = { ...design.jack, ...j };
  if ((design.jack.link || under) && stove) {
    const was = { x: stove.x, z: stove.z };
    const s = snapItem(design, stove, j.x, j.z, []);
    stove.x = Math.abs(s.x - j.x) < 0.15 ? j.x : s.x;
    stove.z = Math.abs(s.z - j.z) < 0.15 ? j.z : s.z;
    const mat = design.items.find((o) => o.type === "mat" && contains(o, was.x, was.z));
    if (mat) { mat.x += stove.x - was.x; mat.z += stove.z - was.z; }
  }
  scene.setDesign(design, { fit: false });
  renderSelection();
}
function dragEnd(hit, ok) {
  if (preDrag) {
    if (ok) {
      undoStack.push(preDrag);
      if (jackIsCustom(design) && !jackIsCustom(JSON.parse(preDrag))) toast("Moving the jack from the front left makes this a custom build. Standard spot puts it back.");
    } else design = JSON.parse(preDrag);
    redoStack.length = 0;
    preDrag = null;
    refresh({ fit: false });
  }
}

// ─── Selection ──────────────────────────────────────────────

function select(sel) {
  selection = sel;
  scene.setSelection(sel);
  renderSelection();
  renderPanel();
}
function selectedItem() {
  return selection?.kind === "item" ? design.items.find((i) => i.id === selection.id) : null;
}
function mutateItem(id, patch) {
  const next = structuredClone(design);
  const it = next.items.find((i) => i.id === id);
  Object.assign(it, patch);
  return next;
}
function removeItem(id) {
  commit({ ...design, items: design.items.filter((i) => i.id !== id) }, { fit: false });
}
function rotateItem(id, by) {
  const it = design.items.find((i) => i.id === id);
  if (CATALOG[it.type].hang) return;
  const next = mutateItem(id, { rot: (((it.rot || 0) + by) % 360 + 360) % 360 });
  const moved = next.items.find((i) => i.id === id);
  const s = snapItem(next, moved, moved.x, moved.z, []);
  moved.x = s.x;
  moved.z = s.z;
  // A sleeping bag on a cot turns with the cot.
  if (CATALOG[it.type].bedTop) {
    for (const bag of next.items) {
      if (!CATALOG[bag.type].bedding || !contains(it, bag.x, bag.z)) continue;
      Object.assign(bag, { x: moved.x, z: moved.z, rot: moved.rot });
    }
  }
  commit(next, { fit: false });
}
function copyItem(id) {
  const it = design.items.find((i) => i.id === id);
  const spot = freeSpot(it.type, it);
  const clone = { ...structuredClone(it), id: newId(), ...spot };
  commit({ ...design, items: [...design.items, clone] }, { fit: false });
  select({ kind: "item", id: clone.id });
}

// The first clear floor, working from the back of the tent to the front.
function freeSpot(type, like = {}) {
  const d = dims(design);
  const test = { type, x: 0, z: 0, rot: like.rot || 0, w: like.w, d: like.d, stove: like.stove };
  const { w, d: dd } = itemSize(test);
  const c = CATALOG[type];
  if (c.hang) return hangSpot(design, test);
  if (c.top) {
    const surf = design.items.find((i) => CATALOG[i.type].surface && !design.items.some((o) => CATALOG[o.type].top && contains(i, o.x, o.z)));
    return surf ? { x: surf.x, z: surf.z } : { x: 0, z: 0 };
  }
  if (c.flat && !c.solid) return { x: 0, z: 0 };
  const solid = design.items.filter((i) => { const k = CATALOG[i.type]; return !k.ghost && !k.top && !k.hang && (!k.flat || k.solid); });
  const clearOf = (x, z) => {
    const e0 = extent({ ...test, x, z });
    if (e0.minX < -d.hw || e0.maxX > d.hw || e0.minZ < -d.hl || e0.maxZ > d.hl) return false;
    const hh = itemSize(test).h;
    if (d.kind === "tipi" && corners({ ...test, x, z }).some(([px, pz]) => d.peak * Math.max(0, 1 - Math.max(Math.abs(px), Math.abs(pz)) / d.hw) < hh)) return false;
    for (const z2 of doorZones(design)) {
      const xs = z2.poly.map((p) => p[0]), zs = z2.poly.map((p) => p[1]);
      if (e0.maxX > Math.min(...xs) && e0.minX < Math.max(...xs) && e0.maxZ > Math.min(...zs) && e0.minZ < Math.max(...zs)) return false;
    }
    return solid.every((o) => {
      const e = extent(o);
      return e0.maxX + 0.1 < e.minX || e0.minX - 0.1 > e.maxX || e0.maxZ + 0.1 < e.minZ || e0.minZ - 0.1 > e.maxZ;
    });
  };
  const best = [];
  for (let z = -d.hl + dd / 2; z <= d.hl - dd / 2 + 1e-6; z += 0.25) {
    for (let x = -d.hw + w / 2; x <= d.hw - w / 2 + 1e-6; x += 0.25) {
      if (clearOf(x, z)) {
        // Along a wall first, so new gear does not land in the aisle.
        const wallGap = Math.min(x - w / 2 + d.hw, d.hw - x - w / 2);
        best.push({ x, z, score: wallGap + Math.abs(z) * 0.06 });
      }
    }
  }
  if (!best.length) return { x: 0, z: 0 };
  best.sort((a, b) => a.score - b.score);
  return { x: best[0].x, z: best[0].z };
}

// The floor under the jack, if nothing is already standing there.
function underJack(extra) {
  const test = { type: "stove", x: design.jack.x, z: design.jack.z, rot: 0, ...extra };
  const e0 = extent(test);
  const clear = design.items.every((o) => {
    const k = CATALOG[o.type];
    if (k.ghost || k.top || k.hang || (k.flat && !k.solid)) return true;
    const e = extent(o);
    return e0.maxX < e.minX || e0.minX > e.maxX || e0.maxZ < e.minZ || e0.minZ > e.maxZ;
  });
  return clear ? { x: test.x, z: test.z } : null;
}

function addItem(type) {
  const extra = type === "stove" ? { stove: stoveFor(dims(design).kind, dims(design).w * dims(design).l) } : CATALOG[type].opts ? { opt: CATALOG[type].opt } : {};
  if (CATALOG[type].hang && dims(design).kind !== "wall") {
    toast(`The ${CATALOG[type].label} ties to a wall tent's frame. A tipi has no frame inside.`);
    return;
  }
  if (type === "stove" && findStove(design)) {
    toast("One stove per jack. Move the stove you have, or remove it first.");
    return;
  }
  if (type === "stove" && tipiHasBackDoor(design)) {
    toast("A tipi with a back door has no stove jack. Turn the back door off under Options to add a stove.");
    return;
  }
  const wall = dims(design).kind === "wall";
  const onBed = CATALOG[type].bedding ? bedSpot(design, { type, id: "" }) : null;
  const spot = type === "stove" ? underJack(extra) ?? freeSpot(type, extra) : onBed ?? freeSpot(type, extra);
  const piece = { id: newId(), type, rot: 0, ...extra, ...spot };
  delete piece.guides;
  // A new stove turns its door into the tent.
  if (type === "stove") piece.rot = stoveRot(dims(design), piece.x, piece.z, piece.stove);
  let next = { ...design, items: [...design.items, piece] };
  if (type === "stove") {
    next = setJackOn(next, true);
    if (!wall) next.jack = { ...next.jack, ...clampJack(next, piece.x, piece.z) };
  }
  commit(next, { fit: false });
  select({ kind: "item", id: piece.id });
  if (scene.getMode() === "outside") scene.setMode("open");
  const full = checks(design).some((c) => c.ids.includes(piece.id) && c.level === "warn");
  if (onBed) toast(`On the ${CATALOG[bedUnder(design, piece)?.type]?.label.toLowerCase() || "cot"}. Drag it off to lay it on the floor.`);
  else if (full) toast(`No clear floor left for the ${CATALOG[type].handle ? CATALOG[type].label : CATALOG[type].label.toLowerCase()}. Move something, or size up.`);
}

// ─── Actions ────────────────────────────────────────────────

function chooseSize(size) {
  if (size === design.size) return;
  const prev = dims(design);
  // Going bigger, the customer can carry their gear over and see the room it leaves.
  const kept = changeSize(design, size);
  const fits = sizeOf(design.model, size).w * sizeOf(design.model, size).l > prev.w * prev.l && !checks(kept).some((c) => c.level === "warn");
  const fresh = resetForSize(design, size);
  parked.clear();
  commit(fresh);
  scene.showGhost(prev, `Was ${prev.w} x ${prev.l}`);
  const [a, b] = size.split("x");
  const layout = templatesFor(design.model, size).cots.label.toLowerCase();
  toast(`${a} x ${b}, default setup: ${layout}. Undo brings your last setup back.`, 6000, fits ? { label: "Keep my gear", run: () => {
    if (design !== fresh) return;
    design = kept;
    refresh({ fit: false });
  } } : null);
}
function chooseModel(key) {
  if (key === design.model) return;
  const before = design.size;
  parked.clear();
  commit(changeModel(design, key));
  if (MODELS[key].kind === "wall" && design.size !== before) toast(`${MODELS[key].name} does not come in ${before.replace("x", " x ")}. Showing ${design.size.replace("x", " x ")}.`);
  if (key === "1890") toast(`The 1890: one door and one rear window. Side windows are ${money(ADDONS.window)} each under Options.`);
  if (key === "tipi1890") toast(`The 1890 Tipi: one zipper door, no window, no screen door, and a sod cloth with no floor. A sewn-in vinyl floor is ${money(ADDONS.vinylFloor.price)} under Options.`);
}
function chooseTemplate(key) {
  const hadBackDoor = design.backDoor;
  commit(placeTemplate(design, key), { fit: false });
  select(null);
  if (hadBackDoor && !design.backDoor) toast("This layout has a stove, so the back door came off. A tipi with a back door has no stove jack.");
}
function set(patch, opts = { fit: false }) {
  commit({ ...design, ...patch }, opts);
}

// ─── Top bar and stage ──────────────────────────────────────

function renderViews() {
  const mode = scene.getMode();
  // The stage knows its view, so a phone can drop the time and setting buttons where they cover the floor.
  $("#stage").dataset.mode = mode;
  $$("[data-view]").forEach((b) => b.classList.toggle("on", b.dataset.view === mode));
  const touch = matchMedia("(pointer: coarse)").matches;
  $("#pad").hidden = mode !== "inside";
  const hints = {
    outside: touch ? "Drag to walk around the tent. Pinch to zoom. Drag a window or the jack to move it." : "Drag to walk around the tent. Scroll to zoom. Drag a window or the stove jack to move it.",
    open: "Drag a piece to move it. Drag anywhere else to look around.",
    plan: "Drawn to scale. Each square is one foot. Drag a piece, a window, or the gold jack ring.",
    inside: touch ? "Drag to look around. Tap the floor to walk there. Tap a piece, then drag it to move it." : "Drag to look around. W, A, S, D or the arrow keys to walk. Click the floor to go there. Click a piece, then drag it to move it.",
  };
  $("#hint").textContent = hints[mode];
}
function renderEnvButtons() {
  const { time, where } = scene.getEnv();
  $$("[data-time]").forEach((b) => b.classList.toggle("on", b.dataset.time === time));
  $$("[data-where]").forEach((b) => b.classList.toggle("on", b.dataset.where === where));
}
function renderStatsHtml(s) {
  const sleeps = s.range[0] === s.range[1] ? s.range[0] : `${s.range[0]} to ${s.range[1]}`;
  const cots = s.moreCots > 19 ? "20+" : s.moreCots;
  const parts = [
    [`${s.area} sq ft`, "Floor"],
    [`${Math.round(s.open)} sq ft`, "Open floor", true],
    [cots ? `${cots} more` : "Full", "Cots that still fit", true],
    [`${s.sleeps} of ${sleeps}`, "Sleeping"],
    [`${Math.round(s.standing)} sq ft`, "Standing room, 6 ft"],
    [ftin(s.peak), "Peak"],
  ];
  if (s.wall) parts.push([ftin(s.wall), "Wall"]);
  return parts.map(([v, k, hi]) => `<div class="stat${hi ? " hi" : ""}"><b>${v}</b><span>${k}</span></div>`).join("");
}
function renderStats() {
  $("#stats").innerHTML = renderStatsHtml(stats(design));
}
let liveTimer = 0;
function renderStatsLive() {
  clearTimeout(liveTimer);
  liveTimer = setTimeout(renderStats, 60);
}
function renderChecks(list) {
  const box = $("#checks");
  box.innerHTML = "";
  const warn = list.filter((c) => c.level === "warn");
  const notes = list.filter((c) => c.level === "note");
  const relevant = selectedItem() ? notes.filter((n) => n.ids.includes(selectedItem().id)) : [];
  const shown = [...warn, ...relevant];
  if (!shown.length) checksOpen = false;
  // One slim line, so the layout stays in view. The pieces are already outlined in red. Open it for the details.
  if (shown.length) {
    const count = warn.length ? `${warn.length} to fix` : "Note";
    box.append(el("button", { class: `checkbar ${warn.length ? "warn" : "note"}`, "aria-expanded": checksOpen ? "true" : "false", onclick: () => { checksOpen = !checksOpen; renderChecks(checks(design)); } },
      el("b", {}, count), el("span", {}, checksOpen ? "Hide" : shown[0].text), el("i", {}, checksOpen ? "▾" : "▴")));
  }
  if (checksOpen) {
    const listBox = el("div", { class: "checklist" });
    for (const c of shown) {
      listBox.append(el("button", { class: `check ${c.level}`, onclick: () => { if (c.ids[0]) select(design.items.some((i) => i.id === c.ids[0]) ? { kind: "item", id: c.ids[0] } : { kind: "window", id: c.ids[0] }); } }, c.text));
    }
    box.prepend(listBox);
  }
  $("#hint").hidden = shown.length > 0;
}
let checksOpen = false;
const inFeet = (v) => ftin(Math.round(v * 12) / 12);
function numInput(value, onchange) {
  return el("input", { type: "number", step: "0.5", min: "1", max: "24", value: String(value), onchange: (e) => onchange(parseFloat(e.target.value)) });
}
function renderSelection() {
  const bar = $("#selbar");
  bar.innerHTML = "";
  bar.hidden = true;
  const it = selectedItem();
  if (it) {
    const c = CATALOG[it.type];
    const s = itemSize(it);
    bar.hidden = false;
    bar.append(el("span", { class: "name" }, it.type === "stove" ? STOVES[stoveKey(it)].label.replace(" Kit", "") + " stove" : c.label), el("span", { class: "size" }, c.hang ? `${inFeet(s.w)} wide, ${inFeet(s.h)} tall, on the wall` : `${inFeet(s.w)} x ${inFeet(s.d)}`));
    if (c.opts) {
      bar.append(el("select", { "aria-label": `${c.label} options`, onchange: (e) => commit(mutateItem(it.id, { opt: e.target.value }), { fit: false }) },
        Object.entries(c.opts).map(([k, o]) => el("option", { value: k, selected: optionOf(it) === o }, `${o.label}, ${money(o.price)}`))));
    }
    if (gearKey(it) && c.published !== false) {
      // Unchecked, the piece stays in the plan and comes off the order, for gear the customer already has.
      const box = el("input", { type: "checkbox", checked: !it.own, onchange: (e) => commit(mutateItem(it.id, { own: !e.target.checked || undefined }), { fit: false }) });
      bar.append(el("label", { class: "onorder" }, box, "On my order"));
    }
    if (it.type === "stove") {
      bar.append(el("select", { "aria-label": "Stove model", onchange: (e) => {
        const next = mutateItem(it.id, { stove: e.target.value });
        commit(next, { fit: false });
      } }, Object.entries(STOVES).map(([k, v]) => el("option", { value: k, selected: k === stoveKey(it) }, v.label))));
    }
    if (c.resize) {
      bar.append(el("label", {}, "W", numInput(s.w, (v) => { if (v >= 1) commit(mutateItem(it.id, { w: v }), { fit: false }); })));
      bar.append(el("label", {}, "D", numInput(s.d, (v) => { if (v >= 1) commit(mutateItem(it.id, { d: v }), { fit: false }); })));
    }
    if (it.type === "stove" && design.jack.on && Math.hypot(it.x - design.jack.x, it.z - design.jack.z) > 0.25) {
      bar.append(el("button", { onclick: () => {
        const next = mutateItem(it.id, { x: design.jack.x, z: design.jack.z });
        commit(next, { fit: false });
      } }, "Put under the jack"));
    }
    if (!c.hang) bar.append(el("button", { onclick: () => rotateItem(it.id, 90) }, "Turn 90"), el("button", { onclick: () => rotateItem(it.id, 15) }, "Turn 15"));
    bar.append(el("button", { onclick: () => copyItem(it.id) }, "Copy"), el("button", { class: "danger", onclick: () => removeItem(it.id) }, "Remove"));
    return;
  }
  if (selection?.kind === "window") {
    const w = design.windows.find((o) => o.id === selection.id);
    if (!w) return;
    bar.hidden = false;
    const names = { L: "Left", R: "Right", B: "Rear", F: "Front" };
    bar.append(el("span", { class: "name" }, w.shape === "tri" ? "Triangular window" : "Window"), el("span", { class: "size" }, `${Math.round(w.w * 12)} x ${Math.round(w.h * 12)} in., ${ftin(w.sill)} off the ground`));
    bar.append(el("select", { "aria-label": "Wall", onchange: (e) => {
      const moved = clampWindow(design, { ...w, wall: e.target.value, u: 0, shape: dims(design).kind === "wall" && (e.target.value === "L" || e.target.value === "R") ? "rect" : w.shape });
      commit({ ...design, windows: design.windows.map((o) => (o.id === w.id ? moved : o)) }, { fit: false });
    } }, Object.entries(names).map(([k, v]) => el("option", { value: k, selected: k === w.wall }, `${v} wall`))));
    bar.append(el("button", { class: "danger", onclick: () => commit({ ...design, windows: design.windows.filter((o) => o.id !== w.id) }, { fit: false }) }, "Remove"));
    return;
  }
  if (selection?.kind === "jack") {
    bar.hidden = false;
    bar.append(el("span", { class: "name" }, "Stove jack"), el("span", { class: "size" }, "5 in. oval"));
    if (dims(design).kind === "wall") {
      bar.append(el("span", { class: "size" }, jackIsCustom(design) ? "Custom spot" : "Standard spot, front left"));
      if (jackIsCustom(design)) bar.append(el("button", { onclick: () => set({ jack: { ...design.jack, ...standardJack(design) } }) }, "Standard spot"));
    } else {
      bar.append(el("span", { class: "size" }, jackAtStandard(design) ? "Back wall, centered" : "Moved from the back wall"));
      if (!jackAtStandard(design)) bar.append(el("button", { onclick: () => commit(tipiJackHome(design), { fit: false }) }, "Back to the back wall"));
    }
  }
}

// ─── Panels ─────────────────────────────────────────────────

function planSvg(model, size, items, big = 64) {
  const s = MODELS[model].sizes[size];
  const pad = 0.6;
  const fake = { w: s.w, l: s.l };
  const scale = big / Math.max(fake.w, fake.l);
  const W = (fake.w + pad * 2) * scale, L = (fake.l + pad * 2) * scale;
  const rects = items.map((it) => {
    const c = CATALOG[it.type];
    if (c.ghost) return "";
    const sz = itemSize(it);
    const x = (it.x + fake.w / 2 + pad) * scale, y = (it.z + fake.l / 2 + pad) * scale;
    const fill = it.type === "stove" ? "#3a3a36" : it.type === "mat" ? "#a6a59c" : c.sleeps ? "#87967A" : c.top ? "#C4AE55" : "#c7b48a";
    return `<rect x="${(-sz.w / 2) * scale}" y="${(-sz.d / 2) * scale}" width="${sz.w * scale}" height="${sz.d * scale}" fill="${fill}" stroke="#3D4C31" stroke-width="0.4" transform="translate(${x} ${y}) rotate(${-(it.rot || 0)})"/>`;
  }).join("");
  const doorW = (MODELS[model].kind === "wall" ? 3.33 : s.door.base * 2) * scale;
  return `<svg viewBox="0 0 ${W} ${L}" width="${W}" height="${L}" aria-hidden="true"><rect x="${pad * scale}" y="${pad * scale}" width="${fake.w * scale}" height="${fake.l * scale}" fill="#F4F0E3" stroke="#3D4C31" stroke-width="1.2"/>${rects}<rect x="${(W - doorW) / 2}" y="${L - pad * scale - 1}" width="${doorW}" height="2.4" fill="#C4AE55"/></svg>`;
}

function renderPanel() {
  $$("[data-tab]").forEach((b) => b.classList.toggle("on", b.dataset.tab === tab));
  track.step("td_tab", { tab }, `tab:${tab}`);
  if (tab === "build" && !track.seen("build")) {
    const b = build(design);
    if (b.quoted) track.build({ setup: setupName(design), total: b.total });
  }
  const body = $("#tabbody");
  const scroll = body.scrollTop;
  body.innerHTML = "";
  ({ tent: tabTent, gear: tabGear, options: tabOptions, build: tabBuild })[tab](body);
  body.scrollTop = scroll;
}

function section(title, sub, ...kids) {
  return el("section", { class: "sect" }, el("h3", {}, title), sub ? el("p", { class: "sub" }, sub) : null, ...kids);
}

function tabTent(body) {
  const m = MODELS[design.model];
  body.append(section("Choose a tent", null,
    el("div", { class: "modelrow" }, MODEL_ORDER.map((k) => MODELS[k]).map((x) => {
      const lowest = x.prices && Math.min(...Object.values(x.prices.duck).flatMap((p) => (Array.isArray(p.own) ? [...p.own, ...p.poles] : Object.values(p))));
      return el("button", { class: `model${x.key === design.model ? " on" : ""}`, onclick: () => chooseModel(x.key) }, x.name, el("small", {}, lowest ? `From ${money(lowest)}` : "Price by quote"));
    })),
    el("p", { class: "blurb" }, m.blurb),
  ));
  const sizes = Object.entries(m.sizes);
  body.append(section("Size", "Each size opens on its default setup. Gear is drawn to scale, so a cot is the same cot in every tent.",
    el("div", { class: "chips" }, sizes.map(([k, s]) => el("button", { class: `chip${k === design.size ? " on" : ""}`, onclick: () => chooseSize(k) }, k.replace("x", " x "), el("small", {}, `${s.w * s.l} sq ft, sleeps ${s.sleeps[0]} to ${s.sleeps[1]}`)))),
  ));
  const t = templatesFor(design.model, design.size);
  const tplBox = el("div", { class: "tpls" });
  for (const [k, v] of Object.entries(t)) {
    const b = el("button", { class: `tpl${design.template === k ? " on" : ""}`, onclick: () => chooseTemplate(k) });
    b.innerHTML = planSvg(design.model, design.size, v.items);
    // A native append prints null as text, so the tools-only tag is added only when it exists.
    b.append(document.createTextNode(v.label), el("small", {}, layoutCount(v.items)));
    if (TOOLS) b.append(el("small", { class: `src ${v.src}` }, v.from));
    tplBox.append(b);
  }
  body.append(section("Choose a layout", `For the ${design.size.replace("x", " x ")}. Pick one, then change anything. Undo brings the last one back.`, tplBox));

  const saved = listSetups();
  body.append(section("Your setups", saved.length ? "Saved in this browser. Open one to pick up where you left off." : "Nothing saved yet. Save keeps this setup so you can look at other sizes and come back.",
    saved.length ? el("div", { class: "setups" }, saved.map((s) => {
      const row = el("div", { class: "setup" });
      const open = el("button", { class: "wn", onclick: () => { commit(s.design); toast(`Opened ${s.name}.`); } });
      open.innerHTML = planSvg(s.design.model, s.design.size, s.design.items, 40);
      open.append(el("span", {}, s.name, el("small", {}, new Date(s.savedAt).toLocaleDateString())));
      row.append(open, el("button", { class: "btn danger", onclick: () => { removeSetup(s.id); renderPanel(); } }, "Remove"));
      return row;
    })) : null,
    el("button", { class: "btn", onclick: openSave }, "Save this setup"),
  ));
}
function layoutCount(items) {
  const sleeps = items.reduce((n, i) => n + (CATALOG[i.type].sleeps || 0), 0);
  const seats = items.filter((i) => i.type === "chair").length;
  return sleeps ? `Sleeps ${sleeps}` : `Seats ${seats}`;
}

const GROUPS = { Ours: "Colorado Tent gear", Sleep: "Sleep", Stove: "Stove", Tables: "Tables", Seating: "Seating", Gear: "Camp gear", Scale: "Scale" };
function gearGlyph(c) {
  const s = 4.6;
  const deep = c.hang ? 0.35 : c.d;
  const w = Math.max(3, Math.min(c.w, 8) * s), d = Math.max(3, Math.min(deep, 8) * s);
  const fill = c.handle ? "#C4AE55" : "#c7b48a";
  return `<svg width="${w + 2}" height="${d + 2}" viewBox="0 0 ${w + 2} ${d + 2}" aria-hidden="true"><rect x="1" y="1" width="${w}" height="${d}" fill="${fill}" stroke="#3D4C31"/></svg>`;
}
// A store piece shows its price, so the customer knows it goes on the build.
function gearPrice(c) {
  if (c.published === false) return "Coming to the store";
  if (c.opts) {
    const prices = Object.values(c.opts).map((o) => o.price);
    return `From ${money(Math.min(...prices))}`;
  }
  return c.price != null ? money(c.price) : null;
}
function tabGear(body) {
  const tipi = dims(design).kind !== "wall";
  body.append(el("p", { class: "note", style: "margin:0 0 12px" }, "Tap to add it. Every piece is drawn at its real size. Each footprint below uses one scale."));
  for (const [g, title] of Object.entries(GROUPS)) {
    const row = el("div", { class: "gears" });
    for (const [k, c] of Object.entries(CATALOG)) {
      if (c.group !== g) continue;
      const off = c.hang && tipi;
      const b = el("button", { class: `gear${c.handle ? " store" : ""}`, disabled: off, onclick: () => addItem(k), title: TOOLS ? `Source: ${c.src}` : c.label });
      b.innerHTML = gearGlyph(c);
      const size = c.hang ? `${inFeet(c.w)} wide, on the wall` : `${inFeet(c.w)} x ${inFeet(c.d)}`;
      b.append(document.createTextNode(c.label), el("small", {}, off ? "Wall tents only" : size));
      if (c.handle && !off) b.append(el("small", { class: "price" }, gearPrice(c)));
      row.append(b);
    }
    const sub = g === "Ours" ? "In the store. Place a piece and it goes on your build. The organizers tie to the frame, flat on the wall, and take no floor." : null;
    body.append(section(title, sub, row));
  }
}

function toggleRow(label, sub, on, change) {
  return el("div", { class: "row" }, el("div", {}, label, sub ? el("small", {}, sub) : null), el("button", { class: `switch${on ? " on" : ""}`, role: "switch", "aria-checked": on ? "true" : "false", "aria-label": label, onclick: () => change(!on) }));
}
function selectRow(label, sub, value, options, change) {
  return el("div", { class: "row" }, el("div", {}, label, sub ? el("small", {}, sub) : null), el("select", { "aria-label": label, onchange: (e) => change(e.target.value) }, options.map(([k, v]) => el("option", { value: k, selected: k === value }, v))));
}
function tabOptions(body) {
  const m = MODELS[design.model];
  const wallKind = m.kind === "wall";
  const d = dims(design);

  if (wallKind) {
    body.append(section("Wall height", `Peak is ${ftin(d.peak)} at this wall height. 5 ft is standard.`,
      el("div", { class: "chips" }, [4, 5, 6].map((w) => el("button", { class: `chip${design.wall === w ? " on" : ""}`, onclick: () => commit(changeWall(design, w), { fit: false }) }, `${w} ft`, el("small", {}, w === 5 ? "Standard" : w === 6 ? "Adds 10 percent" : "Same price")))),
    ));
  }

  const colors = Object.entries(COLORS).filter(([, c]) => c.materials.includes(design.material));
  body.append(section("Canvas", MATERIALS[design.material].note,
    el("div", { class: "chips", style: "margin-bottom:8px" }, Object.entries(MATERIALS).map(([k, v]) => el("button", { class: `chip${design.material === k ? " on" : ""}`, onclick: () => {
      const nextColor = COLORS[design.color].materials.includes(k) ? design.color : Object.keys(COLORS).find((c) => COLORS[c].materials.includes(k));
      set({ material: k, color: nextColor });
    } }, v.label))),
    colors.length > 1 ? el("div", { class: "swatches" }, colors.map(([k, c]) => el("button", { class: `swatch${design.color === k ? " on" : ""}`, title: c.label, "aria-label": c.label, style: `background:${c.hex}`, onclick: () => set({ color: k }) }))) : null,
  ));

  if (wallKind) {
    body.append(section("Frame", FRAMES[design.frame].note,
      el("div", { class: "chips" }, Object.entries(FRAMES).map(([k, f]) => el("button", { class: `chip${design.frame === k ? " on" : ""}`, onclick: () => commit(clearUprights({ ...design, frame: k }), { fit: false }) }, f.label))),
    ));
  } else {
    body.append(section("Poles", "Two outside poles. The floor stays clear.",
      el("div", { class: "chips" }, [["poles", "Include Pole Set"], ["own", "I Have My Own"]].map(([k, v]) => el("button", { class: `chip${design.frame === k ? " on" : ""}`, onclick: () => set({ frame: k }) }, v))),
    ));
  }

  // Back door and screen choices follow the model: sewn screens on the Colorado Tent, the Velcro screen door on The 1890.
  const sd = m.screenDoor;
  const backSub = !wallKind ? `${money(ADDONS.tipiBackDoor[design.model])}${sd === "standard" ? ", with a screen" : ", no screen"}.${m.frontDoor === "3way" ? "" : " Same cut as the front."} No stove jack with a back door.`
    : design.model === "colorado" ? `${money(ADDONS.backDoor)}, or ${money(ADDONS.backDoorScreened[design.size])} with a sewn-in screen. Replaces the rear window.`
    : `${money(ADDONS.backDoor)}. Replaces the rear window.`;
  body.append(section("Doors", (m.frontDoor === "3way" ? "A 3-way zipper: one up the middle and two along the sill to the corners, so the front door opens nearly all the way. " : "")
    + (sd === "standard" ? (wallKind ? "Full screen door, standard." : "Screen door, standard.") : sd === "addon" ? "The doorway has a Velcro outline for the removable screen door." : "One up-and-down zipper door."),
    toggleRow("Front door tied open", null, design.frontOpen, (v) => set({ frontOpen: v })),
    sd === "standard" ? toggleRow("Screen zipped across the door", "Sewn in. Shows when the door is tied open.", design.screen, (v) => set({ screen: v })) : null,
    sd === "addon" ? toggleRow("Removable screen door", `${money(ADDONS.screenDoor.price)}. Fits the Velcro outline.`, design.screen, (v) => set({ screen: v })) : null,
    toggleRow("Back door", backSub, design.backDoor, (v) => {
      const hadStove = !!findStove(design);
      commit(setBackDoor(design, v), { fit: false });
      if (v && !wallKind) toast(hadStove ? "A tipi with a back door has no stove jack, so the stove and mat came out." : "A tipi with a back door has no stove jack.");
    }),
    design.backDoor && design.model === "colorado" ? toggleRow("Sewn-in screen on the back door", `${money(ADDONS.backDoorScreened[design.size])} for the door with its screen`, design.backScreen, (v) => set({ backScreen: v })) : null,
    design.backDoor && sd === "addon" ? toggleRow("Removable screen door for the back", `${money(ADDONS.screenDoor.price)}. The back door has the Velcro outline too.`, design.backScreen, (v) => set({ backScreen: v })) : null,
    design.backDoor ? toggleRow("Back door tied open", null, design.backOpen, (v) => set({ backOpen: v })) : null,
  ));

  const names = { L: "Left wall", R: "Right wall", B: "Rear wall", F: "Front wall" };
  const list = el("div", {});
  for (const w of design.windows) {
    const len = wallLength(d, w.wall);
    const fromEnd = w.wall === "L" || w.wall === "R" ? `${ftin(w.u + len / 2)} from the rear` : Math.abs(w.u) < 0.05 ? "Centered" : `${ftin(Math.abs(w.u))} ${w.u < 0 ? "left" : "right"} of center`;
    const on = selection?.kind === "window" && selection.id === w.id;
    list.append(el("div", { class: `winrow${on ? " on" : ""}` },
      el("button", { class: "wn", onclick: () => select({ kind: "window", id: w.id }) }, `${names[w.wall]}${w.shape === "tri" ? ", triangular" : ""}`, el("small", {}, `${Math.round(w.w * 12)} x ${Math.round(w.h * 12)} in., ${fromEnd}`)),
      el("button", { class: "btn danger", onclick: () => commit({ ...design, windows: design.windows.filter((o) => o.id !== w.id) }, { fit: false }) }, "Remove"),
    ));
  }
  const addWin = (wall) => {
    const w = newWindow(design, wall);
    commit({ ...design, windows: [...design.windows, w] }, { fit: false });
    select({ kind: "window", id: w.id });
    if (design.model === "1890" && (wall === "L" || wall === "R")) toast(`Side windows are ${money(ADDONS.window)} each on The 1890. The Colorado Tent has them standard.`);
  };
  const stockN = stockWindowCount(design);
  const winSub = wallKind
    ? `${stockN === 1 ? "1 window" : `${stockN} windows`} standard. Each added window is ${money(ADDONS.window)}. Drag one in the view to slide it; it snaps to the middle of a frame bay, where the shop drawings put it.`
    : m.stockWindow ? `One triangular window, standard. Left wall with a stove jack, back wall without one. Drag it to move it. Each added window is ${money(ADDONS.window)}.`
    : `No window, standard. Each added window is ${money(ADDONS.window)}.`;
  body.append(section("Windows", winSub,
    design.windows.length ? list : el("p", { class: "note", style: "margin:0" }, "No windows."),
    toggleRow("Window flaps open", "The flaps hang inside and roll down to close.", design.flapsOpen, (v) => set({ flapsOpen: v })),
    el("div", { class: "addrow" }, "Add to:", Object.entries(names).map(([k, v]) => el("button", { class: "btn", onclick: () => addWin(k) }, v.replace(" wall", "")))),
  ));

  const custom = jackIsCustom(design);
  if (tipiHasBackDoor(design)) {
    body.append(section("Stove jack", "No stove jack on a tipi with a back door, so no stove. Turn the back door off to add one."));
  } else body.append(section("Stove jack", wallKind ? (custom ? "5 in. oval. Custom spot, priced by quote." : "5 in. oval, front left. Drag it on the roof to make it custom.") : "5 in. oval, centered on the back wall, clear of the poles. It stays over the stove.",
    toggleRow("Stove jack", wallKind ? "Built in" : `Adds $75 on the ${m.name}`, design.jack.on, (v) => commit(setJackOn(design, v), { fit: false })),
    design.jack.on ? toggleRow("Jack follows the stove", wallKind ? "Custom. Moving the stove moves the jack." : "Keeps the pipe straight", design.jack.link, (v) => {
      const stove = findStove(design);
      if (v && stove) set({ jack: { ...design.jack, link: true, ...clampJack(design, stove.x, stove.z) } });
      else set({ jack: { ...design.jack, link: v } });
    }) : null,
    design.jack.on ? el("div", { class: "stack", style: "margin-top:6px" },
      el("button", { class: "btn", onclick: () => { select({ kind: "jack" }); } }, "Select the jack"),
      wallKind && custom ? el("button", { class: "btn", onclick: () => set({ jack: { ...design.jack, link: false, ...standardJack(design) } }) }, "Back to front left") : null,
      !wallKind && !jackAtStandard(design) ? el("button", { class: "btn", onclick: () => commit(tipiJackHome(design), { fit: false }) }, "Back to the back wall") : null) : null,
  ));

  const floorRow = wallKind ? toggleRow("Wall Tent Floor", design.model === "1890" ? "The 1890 has no D-rings, so it lies loose." : "Ties to the D-rings", design.floor, (v) => set({ floor: v }))
    : m.floor === "sewn" ? el("p", { class: "note", style: "margin:0 0 4px" }, "Sewn-in vinyl floor, standard.")
    : toggleRow("Sewn-in vinyl floor", `${money(ADDONS.vinylFloor.price)}. The 1890 Tipi comes with a sod cloth and no floor.`, design.floor, (v) => set({ floor: v }));
  body.append(section("Floor and cover", null,
    floorRow,
    wallKind ? selectRow("Fly", "Extended runs about 5 ft past the door", design.fly, [["none", "None"], ["regular", "Regular"], ["extended", "Extended"]], (v) => set({ fly: v })) : null,
    toggleRow("On a wood platform", "A deck under the tent", design.deck, (v) => set({ deck: v })),
  ));

  body.append(section("See it better", null,
    toggleRow("Show standing room", "Floor where the canvas clears 6 ft", standingOn, (v) => { standingOn = v; scene.setStanding(v); renderPanel(); }),
    wallKind ? toggleRow("Show the frame", null, frameOn, (v) => { frameOn = v; scene.setFrameVisible(v); renderPanel(); }) : null,
  ));
}
let standingOn = false, frameOn = true;

// Tent options unchecked on the Build tab, with what they were, so checking one puts it back as it was.
// Kept for this visit only. Gear unchecked is kept in the design instead (own), because it stays in the plan.
const parked = new Map();
function uncheckLine(key) {
  const { design: next, saved } = lineOff(design, key);
  if (saved) parked.set(key, saved);
  commit(next, { fit: false });
}
function checkLine(key) {
  const saved = parked.get(key);
  parked.delete(key);
  commit(lineOn(design, key, saved), { fit: false });
}
// Every line on the order, the gear the customer has, and the options they unchecked, in one steady order.
function buildRows(b) {
  const rows = b.lines.map((l) => ({ ...l, on: true }));
  for (const l of b.off) rows.push({ ...l, on: false });
  for (const [key, saved] of [...parked]) {
    const line = rows.some((r) => r.key === key) ? null : build(lineOn(design, key, saved)).lines.find((l) => l.key === key);
    if (line) rows.push({ ...line, on: false });
    else parked.delete(key);
  }
  return rows.sort((p, q) => lineRank(p.key) - lineRank(q.key));
}

function tabBuild(body) {
  const b = build(design);
  const s = stats(design);
  const lines = el("div", {});
  for (const r of buildRows(b)) {
    const fixed = r.key === "tent";
    const box = el("input", { type: "checkbox", checked: r.on, disabled: fixed, "aria-label": fixed ? `${r.label}, the tent` : `${r.label} on this build`, onchange: (e) => (e.target.checked ? checkLine(r.key) : uncheckLine(r.key)) });
    const sub = r.on ? r.sub : r.own ? "In your plan, not on the order" : "Not in this build";
    const name = r.on && r.url ? el("a", { href: r.url, target: "_blank", rel: "noopener" }, r.label) : r.label;
    lines.append(el("div", { class: `line${r.on ? "" : " off"}` }, box, el("div", { class: "lt" }, name, sub ? el("small", {}, sub) : null), el("div", { class: "p" }, r.price == null ? (r.on ? "Quote" : "") : money(r.price))));
  }
  lines.append(el("div", { class: "line tot" }, el("div", {}, b.quoted ? "Total" : "Listed items"), el("div", { class: "p" }, totalText(b))));
  body.append(section("Your build", "List prices from coloradotent.com, and the shop's prices for each change. Uncheck a line to see the price without it. Gear you uncheck stays in your plan, for gear you already have. Freight is added at checkout.", lines));
  if (b.jackNote) body.append(el("p", { class: "small", style: "margin:-8px 0 10px" }, b.jackNote));
  if (b.notes.length) body.append(el("ul", { class: "small notes" }, b.notes.map((n) => el("li", {}, n))));
  const contact = "https://www.coloradotent.com/pages/contact";
  if (b.confirm.length) body.append(el("div", { class: "custom" }, el("b", {}, "We call you to confirm these before production"), el("ul", {}, b.confirm.map((c) => el("li", {}, c)))));
  if (!b.quoted) {
    body.append(el("div", { class: "custom" }, el("b", {}, "Priced by quote"), el("ul", {}, b.custom.map((c) => el("li", {}, c)))));
    body.append(el("a", { class: "cta", href: contact, target: "_blank", rel: "noopener" }, "Request a quote"));
  } else if (b.cart) {
    // Every line is a store product, so the whole build goes to the cart, and the setup rides along in the order note.
    body.append(el("a", { class: "cta", href: buyLink(b), target: "_blank", rel: "noopener", onclick: () => track.buy({ setup: setupName(design), total: b.total, lines: b.lines }) }, "Buy now"));
    if (CONFIG.email) body.append(el("button", { class: "cta alt", onclick: openSave }, "Email me this quote"));
  } else if (!CONFIG.email) {
    // A line the store cannot sell yet, and no email to send the checkout link: the shop takes it by phone.
    body.append(el("a", { class: "cta", href: contact, target: "_blank", rel: "noopener" }, "Request a quote"));
  } else {
    // A line the store cannot sell yet (a draft product), so Ops builds the order and emails the checkout link.
    body.append(el("button", { class: "cta", onclick: openSave }, "Email me a checkout link"));
    body.append(el("p", { class: "small", style: "margin:-4px 0 10px" }, "Every item in this build has a set price. We email a link to check out at this total, plus freight."));
  }
  body.append(el("button", { class: "cta alt", onclick: printBuild }, "Print the plan and build"));
  body.append(el("button", { class: "cta alt", onclick: openSave }, "Save this setup"));
  body.append(el("button", { class: "cta alt", onclick: goCompare }, "Compare with another size"));
  body.append(el("p", { class: "small", style: "margin:10px 0 0" }, `Sleeping ${s.sleeps}. The size guide lists ${s.range[0]} to ${s.range[1]} for this tent. Sewn in Denver since 1890. Handmade finished sizes vary up to 5 percent.`));
  body.append(el("div", { class: "stack", style: "margin-top:12px" },
    el("button", { class: "btn danger", onclick: () => { parked.clear(); commit(newDesign(design.model, design.size, "cots")); } }, "Start over")));

  if (TOOLS) body.append(section("For ads and photos", "Only on the ?tools=1 link. Customers do not see this.",
    el("div", { class: "stack" },
      el("button", { class: "btn", onclick: enterCapture }, "Ad capture"),
      el("button", { class: "btn", onclick: copyTemplate }, "Copy layout as template"),
    ),
    templateOut ? el("textarea", { class: "out", readonly: true }, templateOut) : null,
  ));
}
let templateOut = "";

// The cart link with the setup in the order note, so the shop sees exactly what was designed.
// Shopify shows the note and the attributes on the order (STORE-CHANGES-2026-11-02.md, section 11).
function buyLink(b) {
  const note = [
    `Tent Designer: ${setupName(design)}`,
    ...b.confirm.map((c) => `Confirm by phone: ${c}`),
    ...b.notes.map((c) => `For the shop: ${c}`),
  ].join("\n");
  return `${b.cart}?${new URLSearchParams({ note, "attributes[Setup link]": setupLink(), "attributes[Source]": "Tent Designer" })}`;
}
const setupLink = () => `${location.origin}${location.pathname}#d=${encodeDesign(design)}`;
function goCompare() {
  location.href = `${CONFIG.compare}#a=${encodeDesign(design)}`;
}

// ─── Print ──────────────────────────────────────────────────
// One sheet to take on the trip: the floor plan with every piece's size, the tent as it looks, the build at its
// price, and a packing list with a box to tick for each piece (Harlan, 2026-11-03).

async function printBuild() {
  const out = $("#printout");
  const b = build(design);
  const m = MODELS[design.model];
  const [a, c] = design.size.split("x");
  let view = null;
  try { view = scene.getMode() === "plan" ? null : scene.capture(1500, 900).toDataURL("image/jpeg", 0.88); } catch { /* print without it */ }
  const priceOf = (l) => (l.price == null ? "Quote" : money(l.price));
  const order = el("table", { class: "ptable" }, el("tbody", {},
    b.lines.map((l) => el("tr", {}, el("td", {}, l.label, l.sub ? el("small", {}, l.sub) : null), el("td", { class: "pp" }, priceOf(l)))),
    el("tr", { class: "ptot" }, el("td", {}, b.quoted ? "Total, before freight" : "Listed items"), el("td", { class: "pp" }, totalText(b)))));
  const extras = [
    b.jackNote ? el("p", {}, b.jackNote) : null,
    b.off.length ? el("p", {}, el("b", {}, "In your plan, not on the order: "), b.off.map((l) => l.label).join(", "), ".") : null,
    b.confirm.length ? el("div", {}, el("b", {}, "We call you to confirm these before production"), el("ul", {}, b.confirm.map((x) => el("li", {}, x)))) : null,
    b.notes.length ? el("div", {}, el("b", {}, "For the shop"), el("ul", {}, b.notes.map((x) => el("li", {}, x)))) : null,
    !b.quoted && b.custom.length ? el("div", {}, el("b", {}, "Priced by quote"), el("ul", {}, b.custom.map((x) => el("li", {}, x)))) : null,
  ];
  const packing = el("table", { class: "ptable pack" },
    el("thead", {}, el("tr", {}, el("th", {}, ""), el("th", {}, "Qty"), el("th", {}, "Piece"), el("th", {}, "Size"), el("th", {}, ""))),
    el("tbody", {}, packingList(design).map((r) => el("tr", {}, el("td", {}, el("span", { class: "tick" })), el("td", {}, String(r.n)), el("td", {}, r.name, r.detail ? el("small", {}, r.detail) : null), el("td", {}, r.size), el("td", { class: "from" }, r.from)))));
  out.innerHTML = "";
  out.append(
    el("header", { class: "ph" }, el("img", { src: CONFIG.logoDark, alt: "Colorado Tent" }),
      el("div", {}, el("h1", {}, `${m.name}, ${a} x ${c}`), el("p", {}, `Tent Designer, printed ${new Date().toLocaleDateString()}. Front door at the bottom of the plan.`))),
    el("figure", { class: "pplan", html: drawPlan(design, { title: `Floor plan of the ${m.name}, ${a} by ${c} feet` }) }, el("figcaption", {}, planCaption(design))),
    el("section", { class: "pbuild" }, view ? el("img", { class: "pview", src: view, alt: "The tent in 3D" }) : null,
      el("div", {}, el("h2", {}, "Your build"), order, ...extras)),
    el("section", { class: "ppack" }, el("h2", {}, "Packing list"), el("p", { class: "psub" }, "Every piece in the plan. Tick each one as it goes in the truck."), packing),
    el("footer", {}, "Colorado Tent Company. Sewn in Denver since 1890. (303) 399-3232. coloradotent.com"),
  );
  await Promise.all([...out.querySelectorAll("img")].map((img) => img.decode().catch(() => null)));
  // On the store, the section's print rule hides the store's header, footer, and chat while this class is on.
  document.body.classList.add("td-printing");
  window.addEventListener("afterprint", () => document.body.classList.remove("td-printing"), { once: true });
  window.print();
}

// ─── Save and email ─────────────────────────────────────────

function openSave() {
  track.step("td_save_open");
  const dlg = $("#saveDlg");
  const label = templatesFor(design.model, design.size)[design.template]?.label;
  const name = el("input", { type: "text", maxlength: "60", value: setupName(design, label), "aria-label": "Name this setup" });
  const customer = el("input", { type: "text", maxlength: "80", autocomplete: "name", "aria-label": "Your name" });
  const email = el("input", { type: "email", placeholder: "you@example.com", autocomplete: "email", "aria-label": "Email address" });
  const optIn = el("input", { type: "checkbox" });
  const status = el("p", { class: "dlgstatus", role: "status" });
  const nameOf = () => name.value.trim() || setupName(design, label);
  let savedOnce = false;
  const keep = () => {
    if (savedOnce) return true;
    savedOnce = !!saveSetup(nameOf(), design);
    renderPanel();
    return savedOnce;
  };
  const send = async (e) => {
    e.preventDefault();
    if (!validEmail(email.value)) {
      status.textContent = "Check that email address.";
      email.focus();
      return;
    }
    keep();
    const message = setupEmail({ setup: setupName(design), link: setupLink(), quote: build(design) });
    let result;
    try {
      const b = build(design);
      result = await sendSetup(setupLead({ email: email.value, customer: customer.value, optIn: optIn.checked, name: nameOf(), link: setupLink(), design, total: b.total, custom: [...b.custom, ...b.confirm], attribution: attribution() }));
      if (result.sent) track.lead({ setup: setupName(design), total: b.total });
    } catch {
      result = { sent: false, prototype: false };
    }
    status.innerHTML = "";
    if (result.prototype) {
      status.append("Prototype: no email went out, and nothing went to Ops. The setup is saved in this browser. This is the message a customer would get from sales@.", el("textarea", { class: "out", readonly: true, "aria-label": "Email preview" }, `Subject: ${message.subject}\n\n${message.body}`));
    } else {
      status.textContent = result.sent ? `Sent to ${email.value.trim()}.` : "That did not send. Your setup is saved in this browser. Call (303) 399-3232 and we will send it.";
    }
  };
  dlg.innerHTML = "";
  dlg.append(el("form", { method: "dialog", novalidate: true, onsubmit: send },
    el("div", { class: "dlghead" }, el("h3", {}, "Save this setup"), el("button", { type: "button", class: "btn", onclick: () => dlg.close() }, "Close")),
    el("label", { class: "field" }, "Name this setup", name),
    el("div", { class: "stack" },
      el("button", { type: "button", class: "btn", onclick: () => { status.textContent = keep() ? "Saved in this browser, under Your setups on the Tent tab." : "This browser blocked saving. Copy link keeps it instead."; } }, "Save in this browser"),
      el("button", { type: "button", class: "btn", onclick: () => copyText(setupLink(), "Link copied. It opens this exact setup.") }, "Copy link")),
    CONFIG.email ? [
      el("h4", {}, "Email it to yourself"),
      el("p", { class: "sub" }, build(design).quoted ? "We send your quote, a checkout link at that price, and a link that opens this exact setup on any device." : "We send your build and a link that opens this exact setup on any device. The shop prices the custom work and replies."),
      el("label", { class: "field" }, "Your name (optional)", customer),
      el("label", { class: "field" }, "Email", email),
      el("label", { class: "check-in" }, optIn, "Also send me Colorado Tent news and offers."),
      el("button", { type: "submit", class: "cta" }, "Email me this setup"),
    ] : null,
    status,
  ));
  dlg.showModal();
}
async function copyText(text, done) {
  try {
    await navigator.clipboard.writeText(text);
    toast(done);
  } catch {
    toast("This browser blocked the clipboard. The link is in the address bar.");
  }
}
function copyTemplate() {
  templateOut = JSON.stringify({ model: design.model, size: design.size, items: design.items.map(({ id, ...rest }) => rest), jack: { x: design.jack.x, z: design.jack.z }, windows: design.windows.map(({ id, ...rest }) => rest), wall: design.wall }, (k, v) => (typeof v === "number" ? Math.round(v * 100) / 100 : v));
  copyText(templateOut, "Layout copied. Send it back to make it a template.");
  renderPanel();
}
// A tent with no list price yet reads "By quote" rather than a total of nothing.
const totalText = (b) => (b.lines[0].price == null ? "By quote" : money(b.total) + (b.quoted ? "" : "+"));
function renderTotal() {
  $("#totalAmt").textContent = totalText(build(design));
}

// ─── Ad capture ─────────────────────────────────────────────

const ASPECTS = { "1:1": [2160, 2160], "4:5": [2160, 2700], "9:16": [1620, 2880], "16:9": [3840, 2160] };
let aspect = "4:5";
let spinning = false;
let recording = false;
const logoImg = new Image();
logoImg.src = CONFIG.logo;
let withLogo = true;

function enterCapture() {
  app.classList.add("capture");
  selection = null;
  scene.setSelection(null);
  renderCapture();
}
function exitCapture() {
  app.classList.remove("capture");
  $("#capturebar").hidden = true;
  $("#frameguide").hidden = true;
  spinning = false;
  scene.turntable(0);
}
function renderCapture() {
  if (!app.classList.contains("capture")) return;
  const bar = $("#capturebar");
  bar.hidden = false;
  bar.innerHTML = "";
  const mode = scene.getMode();
  const { time, where } = scene.getEnv();
  for (const k of Object.keys(ASPECTS)) bar.append(el("button", { class: k === aspect ? "on" : "", onclick: () => { aspect = k; renderCapture(); } }, k));
  bar.append(el("span", {}, "|"));
  for (const [k, v] of [["outside", "Outside"], ["open", "Roof off"], ["inside", "Walk in"]]) bar.append(el("button", { class: mode === k ? "on" : "", onclick: () => { scene.setMode(k); renderCapture(); } }, v));
  bar.append(el("span", {}, "|"));
  for (const k of ["day", "dusk", "night"]) bar.append(el("button", { class: time === k ? "on" : "", onclick: () => { scene.setEnv({ time: k }); renderEnvButtons(); renderCapture(); } }, k[0].toUpperCase() + k.slice(1)));
  bar.append(el("span", {}, "|"));
  for (const k of ["meadow", "forest", "snow"]) bar.append(el("button", { class: where === k ? "on" : "", onclick: () => { scene.setEnv({ where: k }); renderEnvButtons(); renderCapture(); } }, { meadow: "Meadow", forest: "Pines", snow: "Snow" }[k]));
  bar.append(el("span", {}, "|"));
  bar.append(el("button", { class: spinning ? "on" : "", onclick: () => { spinning = !spinning; scene.turntable(spinning ? 0.32 : 0); renderCapture(); } }, "Turntable"));
  bar.append(el("button", { class: withLogo ? "on" : "", onclick: () => { withLogo = !withLogo; renderCapture(); } }, "Logo"));
  bar.append(el("button", { class: "go", onclick: saveImage }, "Save image"));
  bar.append(el("button", { class: "go", onclick: recordVideo }, recording ? "Recording" : "Record 12 sec"));
  bar.append(el("button", { onclick: exitCapture }, "Done"));
  drawFrameGuide();
}
function drawFrameGuide() {
  const g = $("#frameguide");
  g.hidden = false;
  g.innerHTML = "";
  const st = $("#stage").getBoundingClientRect();
  const [aw, ah] = ASPECTS[aspect];
  const target = aw / ah;
  let w = st.width, h = st.height;
  if (w / h > target) w = h * target; else h = w / target;
  const x = (st.width - w) / 2, y = (st.height - h) / 2;
  const side = (l, t, wd, ht) => { const i = el("i"); i.style.cssText = `left:${l}px;top:${t}px;width:${wd}px;height:${ht}px`; g.append(i); };
  side(0, 0, st.width, y);
  side(0, y + h, st.width, y);
  side(0, y, x, h);
  side(x + w, y, x, h);
}
function stamp(out) {
  if (!withLogo) return out;
  const ctx = out.getContext("2d");
  const w = out.width * 0.2, h = w * (logoImg.height / logoImg.width);
  ctx.globalAlpha = 0.95;
  ctx.drawImage(logoImg, out.width - w - out.width * 0.035, out.height - h - out.width * 0.035, w, h);
  return out;
}
function saveImage() {
  const [w, h] = ASPECTS[aspect];
  const out = stamp(scene.capture(w, h));
  out.toBlob((blob) => {
    const a = el("a", { href: URL.createObjectURL(blob), download: `colorado-tent-${design.model}-${design.size}-${aspect.replace(":", "x")}.png` });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }, "image/png");
}
function recordVideo() {
  if (recording) return;
  const canvas = $("#view");
  if (!canvas.captureStream || !window.MediaRecorder) {
    toast("This browser cannot record the canvas. Use Chrome or Edge.");
    return;
  }
  const type = ["video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=vp9", "video/webm"].find((t) => MediaRecorder.isTypeSupported(t));
  const rec = new MediaRecorder(canvas.captureStream(30), { mimeType: type, videoBitsPerSecond: 14000000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  rec.onstop = () => {
    recording = false;
    scene.turntable(0);
    spinning = false;
    renderCapture();
    const a = el("a", { href: URL.createObjectURL(new Blob(chunks, { type })), download: `colorado-tent-${design.model}-${design.size}.${type.includes("mp4") ? "mp4" : "webm"}` });
    document.body.append(a);
    a.click();
    a.remove();
  };
  recording = true;
  renderCapture();
  scene.turntable((Math.PI * 2) / 12);
  rec.start();
  setTimeout(() => rec.stop(), 12000);
}

// ─── Wiring ─────────────────────────────────────────────────

$$("[data-view]").forEach((b) => b.addEventListener("click", () => scene.setMode(b.dataset.view)));
$$("[data-time]").forEach((b) => b.addEventListener("click", () => { scene.setEnv({ time: b.dataset.time }); renderEnvButtons(); }));
$$("[data-where]").forEach((b) => b.addEventListener("click", () => { scene.setEnv({ where: b.dataset.where }); renderEnvButtons(); }));
$$("[data-tab]").forEach((b) => b.addEventListener("click", () => { tab = b.dataset.tab; $("#tabbody").scrollTop = 0; renderPanel(); }));
$("#undo").addEventListener("click", undo);
$("#redo").addEventListener("click", redo);
$("#share").addEventListener("click", () => copyText(location.href, "Link copied. It opens this exact layout."));
$("#save").addEventListener("click", openSave);
$("#compare").addEventListener("click", goCompare);
$("#totalBtn").addEventListener("click", () => { tab = "build"; renderPanel(); });
$$("#pad button").forEach((b) => {
  const k = b.dataset.k;
  b.addEventListener("pointerdown", (e) => { e.preventDefault(); b.setPointerCapture?.(e.pointerId); scene.keyDown(k); });
  for (const type of ["pointerup", "pointercancel", "lostpointercapture"]) b.addEventListener(type, () => scene.keyUp(k));
});
// Inside the store's shadow root a key event reaches the window with the host as its target, so read the real one.
const typing = (e) => ["INPUT", "SELECT", "TEXTAREA"].includes((e.composedPath?.()[0] || e.target).tagName);
window.addEventListener("keydown", (e) => {
  if (typing(e)) return;
  const k = e.key.toLowerCase();
  if ((e.metaKey || e.ctrlKey) && k === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
  if (e.metaKey || e.ctrlKey) return;
  if (app.classList.contains("capture") && k === "escape") { exitCapture(); return; }
  if (scene.getMode() === "inside" && ["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k)) {
    e.preventDefault();
    scene.keyDown(k);
    return;
  }
  const it = selectedItem();
  if (it) {
    const step = e.shiftKey ? 1 : 0.25;
    const nudge = { arrowleft: [-step, 0], arrowright: [step, 0], arrowup: [0, -step], arrowdown: [0, step] }[k];
    if (nudge) {
      e.preventDefault();
      const s = snapItem(design, it, it.x + nudge[0], it.z + nudge[1], []);
      commit(mutateItem(it.id, { x: s.x, z: s.z, ...(s.rot != null ? { rot: s.rot } : {}) }), { fit: false });
      return;
    }
    if (k === "r") { rotateItem(it.id, e.shiftKey ? 15 : 90); return; }
    if (k === "delete" || k === "backspace") { e.preventDefault(); removeItem(it.id); return; }
    if (k === "escape") { select(null); return; }
  }
  const view = { 1: "outside", 2: "open", 3: "plan", 4: "inside" }[k];
  if (view) scene.setMode(view);
});
window.addEventListener("keyup", (e) => scene.keyUp(e.key.toLowerCase()));
window.addEventListener("blur", () => ["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].forEach((k) => scene.keyUp(k)));
window.addEventListener("resize", () => { if (app.classList.contains("capture")) drawFrameGuide(); });

// ─── First paint ────────────────────────────────────────────

const validTime = (t) => (["day", "dusk", "night"].includes(t) ? t : null);
const validWhere = (w) => (["meadow", "forest", "snow"].includes(w) ? w : null);
scene.setEnv({ time: validTime(params.get("time")) || "day", where: validWhere(params.get("scene")) || "meadow" });
refresh();
// start= picks the first view. On the store it has to be start: Shopify takes ?view= to mean an alternate page
// template, and the page falls back to a blank one (2026-11-03). view= still works on the standalone page.
const startView = { outside: "outside", open: "open", plan: "plan", walk: "inside", inside: "inside" }[params.get("start") || params.get("view")] || "outside";
scene.setMode(startView, { snap: true, yaw: -0.68 });
renderViews();
renderEnvButtons();
if (params.get("spin") === "1") scene.turntable(0.3);
if (params.get("capture") === "1") enterCapture();
if (params.get("embed") === "1") app.classList.add("capture");

// For tuning: open the console and call tentDesigner.
window.tentDesigner = { get design() { return design; }, scene, sizeOf };
